import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  DROPDOWN_VIEWPORT_MARGIN,
  DROPDOWN_Z_INDEX,
} from '../../hooks/useFloatingPanelPosition';
import {
  dropdownPanelVariantsForPlacement,
  dropdownTransition,
} from './dropdownMotion';
import {
  calendarBackdropVariants,
  calendarDayCellVariants,
  calendarGridItemVariants,
  calendarSheetVariants,
  calendarSlideVariants,
  calendarTitleVariants,
  calendarTransition,
  iosQuartOut,
  iosSheetSpring,
} from './calendarMotion';

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const MIN_AGE_YEARS = 13;
const MAX_AGE_YEARS = 120;
const CALENDAR_PANEL_ESTIMATED_HEIGHT = 360;
const CALENDAR_MODE_MIN_HEIGHT = {
  days: 320,
  months: 268,
  years: 268,
};
const MOBILE_MEDIA_QUERY = '(max-width: 639px)';

function toIsoDate(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function buildCalendarCells(year, month) {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  for (let i = 0; i < firstWeekday; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(day);
  }
  return cells;
}

function todayParts() {
  const today = new Date();
  return {
    iso: toIsoDate(today.getFullYear(), today.getMonth(), today.getDate()),
  };
}

function getMinBirthIso() {
  const today = new Date();
  return toIsoDate(today.getFullYear() - MAX_AGE_YEARS, today.getMonth(), today.getDate());
}

function getMaxBirthIso() {
  const today = new Date();
  return toIsoDate(today.getFullYear() - MIN_AGE_YEARS, today.getMonth(), today.getDate());
}

function isDateSelectable(iso, minIso, maxIso) {
  return iso >= minIso && iso <= maxIso;
}

function computeCalendarPanelPosition(anchorRect, panelHeight, gap = 8) {
  const margin = DROPDOWN_VIEWPORT_MARGIN;
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const spaceBelow = viewportHeight - anchorRect.bottom - margin;
  const spaceAbove = anchorRect.top - margin;
  const naturalHeight = Math.max(panelHeight, 200);

  let placement = 'bottom';
  if (naturalHeight + gap > spaceBelow && spaceAbove >= spaceBelow) {
    placement = 'top';
  }

  let available = placement === 'bottom' ? spaceBelow - gap : spaceAbove - gap;
  available = Math.max(available, 200);
  const maxHeight = Math.min(naturalHeight, available);

  let top =
    placement === 'bottom'
      ? anchorRect.bottom + gap
      : anchorRect.top - maxHeight - gap;

  if (top + maxHeight > viewportHeight - margin) {
    top = viewportHeight - margin - maxHeight;
  }
  if (top < margin) {
    top = margin;
  }

  const width = Math.min(Math.max(anchorRect.width, 280), viewportWidth - margin * 2);
  const left = Math.min(Math.max(margin, anchorRect.left), viewportWidth - width - margin);

  return { top, left, width, placement, maxHeight };
}

function estimatePanelHeight(pickerMode) {
  return CALENDAR_MODE_MIN_HEIGHT[pickerMode] || CALENDAR_PANEL_ESTIMATED_HEIGHT;
}

function useMobileLayout() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_MEDIA_QUERY);
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return isMobile;
}

function getBirthYearBounds() {
  const today = new Date();
  return {
    minYear: today.getFullYear() - MAX_AGE_YEARS,
    maxYear: today.getFullYear() - MIN_AGE_YEARS,
  };
}

function isMonthSelectable(year, month, minIso, maxIso) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let day = 1; day <= daysInMonth; day += 1) {
    if (isDateSelectable(toIsoDate(year, month, day), minIso, maxIso)) {
      return true;
    }
  }
  return false;
}

function getMonthLabels(locale) {
  return Array.from({ length: 12 }, (_, month) =>
    new Date(2000, month, 1).toLocaleDateString(locale, { month: 'short' })
  );
}

function getYearPageStart(viewYear, minYear, maxYear, pageSize = 12) {
  const clamped = Math.max(minYear, Math.min(viewYear, maxYear));
  const pageStart = Math.floor((clamped - minYear) / pageSize) * pageSize + minYear;
  return Math.max(minYear, Math.min(pageStart, Math.max(minYear, maxYear - pageSize + 1)));
}

function CalendarHeader({
  pickerMode,
  viewYear,
  monthLabel,
  prevLabel,
  nextLabel,
  selectMonthYearLabel,
  onPrev,
  onNext,
  onTitleClick,
  reduceMotion,
}) {
  const { minYear, maxYear } = getBirthYearBounds();
  const yearPageStart = getYearPageStart(viewYear, minYear, maxYear);
  const yearPageEnd = Math.min(yearPageStart + 11, maxYear);

  let title = monthLabel;
  if (pickerMode === 'months') {
    title = String(viewYear);
  } else if (pickerMode === 'years') {
    title = yearPageStart === yearPageEnd ? String(yearPageStart) : `${yearPageStart} – ${yearPageEnd}`;
  }

  const titleKey = `${pickerMode}-${title}`;

  return (
    <div className="mb-3 flex items-center justify-between gap-2">
      <motion.button
        type="button"
        onClick={onPrev}
        whileTap={reduceMotion ? undefined : { scale: 0.9 }}
        transition={calendarTransition(reduceMotion)}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-secondary-600 transition-colors hover:bg-orange-50 hover:text-orange-600   "
        aria-label={prevLabel}
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </motion.button>
      <button
        type="button"
        onClick={onTitleClick}
        className="relative min-h-10 min-w-0 flex-1 overflow-hidden rounded-xl px-2 py-2 text-center transition-colors hover:bg-orange-50 "
        aria-label={selectMonthYearLabel}
        aria-expanded={pickerMode !== 'days'}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={titleKey}
            initial="enter"
            animate="center"
            exit="exit"
            variants={calendarTitleVariants}
            transition={calendarTransition(reduceMotion)}
            className="block text-sm font-semibold capitalize text-secondary-900  sm:text-base"
          >
            {title}
          </motion.span>
        </AnimatePresence>
      </button>
      <motion.button
        type="button"
        onClick={onNext}
        whileTap={reduceMotion ? undefined : { scale: 0.9 }}
        transition={calendarTransition(reduceMotion)}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-secondary-600 transition-colors hover:bg-orange-50 hover:text-orange-600   "
        aria-label={nextLabel}
      >
        <ChevronRightIcon className="h-5 w-5" />
      </motion.button>
    </div>
  );
}

function MonthPickerGrid({ viewYear, viewMonth, locale, minIso, maxIso, onSelectMonth, reduceMotion }) {
  const monthLabels = useMemo(() => getMonthLabels(locale), [locale]);

  return (
    <motion.div
      className="grid grid-cols-3 gap-2 sm:gap-2.5"
      initial="hidden"
      animate="visible"
      variants={{
        visible: { transition: { staggerChildren: reduceMotion ? 0 : 0.018 } },
      }}
    >
      {monthLabels.map((label, month) => {
        const selectable = isMonthSelectable(viewYear, month, minIso, maxIso);
        const isCurrent = month === viewMonth;

        return (
          <motion.button
            key={label}
            type="button"
            custom={month}
            initial="hidden"
            animate="visible"
            variants={calendarGridItemVariants}
            disabled={!selectable}
            onClick={() => onSelectMonth(month)}
            whileTap={selectable && !reduceMotion ? { scale: 0.94 } : undefined}
            transition={calendarTransition(reduceMotion)}
            className={`rounded-xl px-2 py-3 text-sm font-semibold capitalize transition-colors ${
              !selectable
                ? 'cursor-not-allowed text-secondary-300 '
                : isCurrent
                  ? 'bg-orange-500 text-white shadow-md'
                  : 'text-secondary-900 hover:bg-orange-50  '
            }`}
          >
            {label}
          </motion.button>
        );
      })}
    </motion.div>
  );
}

function YearPickerGrid({ viewYear, onSelectYear, reduceMotion }) {
  const { minYear, maxYear } = getBirthYearBounds();
  const pageStart = getYearPageStart(viewYear, minYear, maxYear);
  const years = Array.from({ length: 12 }, (_, index) => pageStart + index).filter((year) => year <= maxYear);

  return (
    <motion.div
      className="grid grid-cols-3 gap-2 sm:gap-2.5"
      initial="hidden"
      animate="visible"
      variants={{
        visible: { transition: { staggerChildren: reduceMotion ? 0 : 0.018 } },
      }}
    >
      {years.map((year, index) => {
        const selectable = year >= minYear && year <= maxYear;
        const isCurrent = year === viewYear;

        return (
          <motion.button
            key={year}
            type="button"
            custom={index}
            initial="hidden"
            animate="visible"
            variants={calendarGridItemVariants}
            disabled={!selectable}
            onClick={() => onSelectYear(year)}
            whileTap={selectable && !reduceMotion ? { scale: 0.94 } : undefined}
            transition={calendarTransition(reduceMotion)}
            className={`rounded-xl px-2 py-3 text-sm font-semibold transition-colors ${
              !selectable
                ? 'cursor-not-allowed text-secondary-300 '
                : isCurrent
                  ? 'bg-orange-500 text-white shadow-md'
                  : 'text-secondary-900 hover:bg-orange-50  '
            }`}
          >
            {year}
          </motion.button>
        );
      })}
    </motion.div>
  );
}

function CalendarGrid({
  viewYear,
  viewMonth,
  value,
  minIso,
  maxIso,
  todayIso,
  weekdayLabels,
  locale,
  pickerMode,
  onPrev,
  onNext,
  onTitleClick,
  onSelectDay,
  onSelectMonth,
  onSelectYear,
  monthLabel,
  prevLabel,
  nextLabel,
  selectMonthYearLabel,
  slideDirection,
  compact = false,
}) {
  const reduceMotion = useReducedMotion();
  const calendarCells = useMemo(
    () => buildCalendarCells(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  const slideVariants = calendarSlideVariants(reduceMotion);
  const transition = calendarTransition(reduceMotion);
  const daysKey = `days-${viewYear}-${viewMonth}`;
  const modeKey = pickerMode === 'days' ? daysKey : `${pickerMode}-${viewYear}`;
  const contentMinHeight = CALENDAR_MODE_MIN_HEIGHT[pickerMode] || CALENDAR_MODE_MIN_HEIGHT.days;

  return (
    <>
      <CalendarHeader
        pickerMode={pickerMode}
        viewYear={viewYear}
        monthLabel={monthLabel}
        prevLabel={prevLabel}
        nextLabel={nextLabel}
        selectMonthYearLabel={selectMonthYearLabel}
        onPrev={onPrev}
        onNext={onNext}
        onTitleClick={onTitleClick}
        reduceMotion={reduceMotion}
      />

      <div
        className="relative overflow-hidden"
        style={{ minHeight: contentMinHeight }}
      >
        <AnimatePresence mode="wait" initial={false} custom={slideDirection}>
          {pickerMode === 'days' ? (
            <motion.div
              key={daysKey}
              custom={slideDirection}
              initial="enter"
              animate="center"
              exit="exit"
              variants={slideVariants}
              transition={transition}
              className={`grid grid-cols-7 gap-0.5 text-center sm:gap-1 ${compact ? '' : ''}`}
            >
              {weekdayLabels.map((label) => (
                <div
                  key={label}
                  className="py-1 text-[10px] font-semibold uppercase tracking-wide text-secondary-500  sm:text-[11px]"
                >
                  {label}
                </div>
              ))}
              {calendarCells.map((day, index) => {
                if (!day) {
                  return <div key={`empty-${index}`} className="h-10 sm:h-11" />;
                }

                const iso = toIsoDate(viewYear, viewMonth, day);
                const selectable = isDateSelectable(iso, minIso, maxIso);
                const isSelected = iso === value;
                const isToday = iso === todayIso;

                return (
                  <motion.button
                    key={iso}
                    type="button"
                    custom={index}
                    initial={reduceMotion ? false : 'hidden'}
                    animate="visible"
                    variants={calendarDayCellVariants}
                    disabled={!selectable}
                    onClick={() => onSelectDay(day)}
                    whileTap={selectable && !reduceMotion ? { scale: 0.9 } : undefined}
                    transition={calendarTransition(reduceMotion)}
                    className={`relative flex h-10 items-center justify-center rounded-xl text-sm font-semibold transition-colors sm:h-11 ${
                      !selectable
                        ? 'cursor-not-allowed text-secondary-300 '
                        : isSelected
                          ? 'bg-orange-500 text-white shadow-md'
                          : isToday
                            ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-300   '
                            : 'text-secondary-900 hover:bg-orange-50  '
                    }`}
                    aria-label={iso}
                    aria-pressed={isSelected}
                  >
                    {day}
                  </motion.button>
                );
              })}
            </motion.div>
          ) : null}

          {pickerMode === 'months' ? (
            <motion.div
              key={modeKey}
              custom={slideDirection}
              initial="enter"
              animate="center"
              exit="exit"
              variants={slideVariants}
              transition={transition}
            >
              <MonthPickerGrid
                viewYear={viewYear}
                viewMonth={viewMonth}
                locale={locale}
                minIso={minIso}
                maxIso={maxIso}
                onSelectMonth={onSelectMonth}
                reduceMotion={reduceMotion}
              />
            </motion.div>
          ) : null}

          {pickerMode === 'years' ? (
            <motion.div
              key={modeKey}
              custom={slideDirection}
              initial="enter"
              animate="center"
              exit="exit"
              variants={slideVariants}
              transition={transition}
            >
              <YearPickerGrid
                viewYear={viewYear}
                onSelectYear={onSelectYear}
                reduceMotion={reduceMotion}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </>
  );
}

function BirthDatePicker({
  value = '',
  onChange,
  id: idProp,
  required = false,
  disabled = false,
  className = '',
  inputClassName = '',
}) {
  const generatedId = useId();
  const inputId = idProp || generatedId;
  const { t, locale } = useLanguage();
  const r = t.referral;
  const reduceMotion = useReducedMotion();
  const isMobile = useMobileLayout();
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState(null);

  const today = useMemo(() => todayParts(), []);
  const minIso = useMemo(() => getMinBirthIso(), []);
  const maxIso = useMemo(() => getMaxBirthIso(), []);

  const initialView = useMemo(() => {
    if (value) {
      const [year, month] = value.split('-').map(Number);
      return { year, month: month - 1 };
    }
    const [maxYear, maxMonth] = maxIso.split('-').map(Number);
    return { year: maxYear, month: maxMonth - 1 };
  }, [value, maxIso]);

  const [viewYear, setViewYear] = useState(initialView.year);
  const [viewMonth, setViewMonth] = useState(initialView.month);
  const [pickerMode, setPickerMode] = useState('days');
  const [slideDirection, setSlideDirection] = useState(1);

  const { minYear, maxYear } = useMemo(() => getBirthYearBounds(), []);

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });

  const weekdayLabels = WEEKDAY_KEYS.map((key) => r.calendarWeekdays?.[key] || key.toUpperCase());

  const displayValue = value
    ? new Date(`${value}T12:00:00`).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  const closePicker = useCallback(() => {
    setOpen(false);
    setPickerMode('days');
    setSlideDirection(1);
  }, []);

  useEffect(() => {
    if (!open) {
      setPickerMode('days');
      setSlideDirection(1);
    }
  }, [open]);

  const placement = panelPosition?.placement ?? 'bottom';
  const panelVariants = dropdownPanelVariantsForPlacement(placement);
  const transition = dropdownTransition(reduceMotion);

  useLayoutEffect(() => {
    if (!open || isMobile || !buttonRef.current) {
      setPanelPosition(null);
      return undefined;
    }

    function update() {
      const anchorRect = buttonRef.current.getBoundingClientRect();
      const panelEl = panelRef.current;
      const measuredHeight = panelEl
        ? Math.max(panelEl.scrollHeight, panelEl.offsetHeight)
        : estimatePanelHeight(pickerMode);
      const fallbackHeight = estimatePanelHeight(pickerMode);

      setPanelPosition(
        computeCalendarPanelPosition(anchorRect, Math.max(measuredHeight, fallbackHeight))
      );
    }

    update();
    const raf = window.requestAnimationFrame(update);

    const panelEl = panelRef.current;
    let resizeObserver;
    if (panelEl && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(update);
      });
      resizeObserver.observe(panelEl);
    }

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.cancelAnimationFrame(raf);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, isMobile, viewYear, viewMonth, pickerMode, slideDirection]);

  useEffect(() => {
    if (!open || isMobile || !buttonRef.current) return undefined;

    function reposition() {
      if (!buttonRef.current || !panelRef.current) return;
      const anchorRect = buttonRef.current.getBoundingClientRect();
      const measuredHeight = Math.max(
        panelRef.current.scrollHeight,
        panelRef.current.offsetHeight,
        estimatePanelHeight(pickerMode)
      );
      setPanelPosition(computeCalendarPanelPosition(anchorRect, measuredHeight));
    }

    reposition();
    const timers = [80, 320].map((delay) => window.setTimeout(reposition, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [pickerMode, slideDirection, open, isMobile]);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      const inButton = buttonRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inButton && !inPanel) {
        closePicker();
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        closePicker();
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, closePicker]);

  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, isMobile]);

  function goPrev() {
    setSlideDirection(-1);
    if (pickerMode === 'days') {
      if (viewMonth === 0) {
        setViewMonth(11);
        setViewYear((year) => Math.max(minYear, year - 1));
      } else {
        setViewMonth((month) => month - 1);
      }
      return;
    }

    if (pickerMode === 'months') {
      setViewYear((year) => Math.max(minYear, year - 1));
      return;
    }

    const pageStart = getYearPageStart(viewYear, minYear, maxYear);
    setViewYear(Math.max(minYear, pageStart - 12));
  }

  function goNext() {
    setSlideDirection(1);
    if (pickerMode === 'days') {
      if (viewMonth === 11) {
        setViewMonth(0);
        setViewYear((year) => Math.min(maxYear, year + 1));
      } else {
        setViewMonth((month) => month + 1);
      }
      return;
    }

    if (pickerMode === 'months') {
      setViewYear((year) => Math.min(maxYear, year + 1));
      return;
    }

    const pageStart = getYearPageStart(viewYear, minYear, maxYear);
    setViewYear(Math.min(maxYear, pageStart + 12));
  }

  function handleTitleClick() {
    if (pickerMode === 'days') {
      setSlideDirection(1);
      setPickerMode('months');
      return;
    }
    if (pickerMode === 'months') {
      setSlideDirection(1);
      setPickerMode('years');
      return;
    }
    setSlideDirection(-1);
    setPickerMode('months');
  }

  function handleSelectMonth(month) {
    if (!isMonthSelectable(viewYear, month, minIso, maxIso)) return;
    setSlideDirection(-1);
    setViewMonth(month);
    setPickerMode('days');
  }

  function handleSelectYear(year) {
    if (year < minYear || year > maxYear) return;
    setSlideDirection(-1);
    setViewYear(year);
    setPickerMode('months');
  }

  function handleSelectDay(day) {
    const iso = toIsoDate(viewYear, viewMonth, day);
    if (!isDateSelectable(iso, minIso, maxIso)) return;
    onChange?.(iso);
    closePicker();
  }

  const calendarProps = {
    viewYear,
    viewMonth,
    value,
    minIso,
    maxIso,
    todayIso: today.iso,
    weekdayLabels,
    locale,
    pickerMode,
    onPrev: goPrev,
    onNext: goNext,
    onTitleClick: handleTitleClick,
    onSelectDay: handleSelectDay,
    onSelectMonth: handleSelectMonth,
    onSelectYear: handleSelectYear,
    monthLabel,
    prevLabel: r.calendarPrevMonth,
    nextLabel: r.calendarNextMonth,
    selectMonthYearLabel: r.calendarSelectMonthYear,
    slideDirection,
  };

  const desktopPanel =
    open && !isMobile && panelPosition ? (
      <motion.div
        ref={panelRef}
        role="dialog"
        aria-label={r.dateOfBirth}
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={panelVariants}
        transition={transition}
        style={{
          position: 'fixed',
          top: panelPosition.top,
          left: panelPosition.left,
          width: panelPosition.width,
          minHeight: estimatePanelHeight(pickerMode),
          maxHeight: panelPosition.maxHeight,
          zIndex: DROPDOWN_Z_INDEX,
        }}
        className={`overflow-y-auto overscroll-contain rounded-2xl border border-secondary-200/80 bg-white p-3 shadow-premium   sm:p-4 ${
          placement === 'top' ? 'origin-bottom' : 'origin-top'
        }`}
        onAnimationComplete={() => {
          if (!buttonRef.current || !panelRef.current) return;
          const anchorRect = buttonRef.current.getBoundingClientRect();
          const measuredHeight = Math.max(
            panelRef.current.scrollHeight,
            panelRef.current.offsetHeight,
            estimatePanelHeight(pickerMode)
          );
          setPanelPosition(computeCalendarPanelPosition(anchorRect, measuredHeight));
        }}
      >
        <CalendarGrid {...calendarProps} />
      </motion.div>
    ) : null;

  const mobileSheet =
    open && isMobile ? (
      <>
        <motion.button
          type="button"
          aria-label={r.calendarClose}
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={calendarBackdropVariants}
          transition={reduceMotion ? { duration: 0.01 } : iosQuartOut}
          className="fixed inset-0 z-[10019] bg-black/10"
          onClick={closePicker}
        />
        <motion.div
          ref={panelRef}
          role="dialog"
          aria-label={r.dateOfBirth}
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={calendarSheetVariants}
          transition={reduceMotion ? { duration: 0.01 } : iosSheetSpring}
          className="fixed inset-x-0 bottom-0 max-h-[min(92dvh,560px)] overflow-y-auto overscroll-contain rounded-t-3xl border border-secondary-200/90 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-8px_40px_rgba(15,23,42,0.12)]   "
          style={{
            zIndex: DROPDOWN_Z_INDEX,
            minHeight: estimatePanelHeight(pickerMode) + 72,
          }}
        >
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, scaleX: 0.6 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={calendarTransition(reduceMotion)}
            className="mx-auto mb-3 h-1 w-10 origin-center rounded-full bg-secondary-200 "
            aria-hidden="true"
          />
          <p className="mb-3 text-center text-sm font-semibold text-secondary-900 ">
            {r.dateOfBirth}
          </p>
          <CalendarGrid {...calendarProps} compact />
        </motion.div>
      </>
    ) : null;

  return (
    <div className={className}>
      <motion.button
        ref={buttonRef}
        type="button"
        id={inputId}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-required={required}
        onClick={() => !disabled && setOpen((current) => !current)}
        whileTap={!disabled && !reduceMotion ? { scale: 0.99 } : undefined}
        transition={calendarTransition(reduceMotion)}
        className={`input-premium relative flex w-full items-center gap-2 py-2 pl-9 pr-3 text-left text-sm max-lg:py-2.5 max-lg:pl-8 max-lg:text-sm lg:py-3 lg:pl-12 ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        } ${inputClassName}`.trim()}
      >
        <CalendarDaysIcon
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-orange-500 max-lg:h-4 max-lg:w-4 lg:left-4 lg:h-5 lg:w-5"
          aria-hidden="true"
        />
        <span
          className={`min-w-0 truncate ${
            displayValue ? 'text-secondary-900 ' : 'text-secondary-400 '
          }`}
        >
          {displayValue || r.dateOfBirthPlaceholder}
        </span>
      </motion.button>

      {required ? (
        <input
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute h-0 w-0 opacity-0"
          value={value}
          onChange={() => {}}
          required
        />
      ) : null}

      {typeof document !== 'undefined'
        ? createPortal(
            <AnimatePresence>
              {desktopPanel}
              {mobileSheet}
            </AnimatePresence>,
            document.body
          )
        : null}
    </div>
  );
}

export default BirthDatePicker;
